<template>
  <template v-if="bodyVariantDesktop">
    <component
      :is="inline ? 'span' : 'div'"
      :class="['rte', inline ? undefined : 'block md:hidden']"
      data-component="RichText">
      <RichTextElements :elements="displayedElements" :body-variant="bodyVariant" />
    </component>
    <component
      :is="inline ? 'span' : 'div'"
      :class="['rte', inline ? undefined : 'hidden md:block']"
      data-component="RichText">
      <RichTextElements :elements="displayedElements" :body-variant="bodyVariantDesktop" />
    </component>
  </template>
  <template v-else>
    <component :is="inline ? 'span' : 'div'" class="rte" data-component="RichText">
      <RichTextElements :elements="displayedElements" :body-variant="bodyVariant" />
    </component>
  </template>
</template>

<script setup lang="ts">
import type { UmbracoRichText, UmbracoRichTextElement } from '@gro-up/types'
import { provide, computed } from 'vue'

import type { TypographyVariant } from '../Typography/Typography.vue'

import RichTextElements from './RichTextElements.vue'

export type RichTextProps = {
  text: UmbracoRichText
  bodyVariant?: TypographyVariant
  bodyVariantDesktop?: TypographyVariant
  inline?: boolean
}

const props = withDefaults(defineProps<RichTextProps>(), {
  bodyVariant: 'body',
  bodyVariantDesktop: undefined,
  inline: false,
})

const inline = computed(() => props.inline)

type UmbracoRichTextElementWithChildren = Extract<UmbracoRichTextElement, { elements: UmbracoRichTextElement[] }>

const hasElements = (element: UmbracoRichTextElement): element is UmbracoRichTextElementWithChildren =>
  'elements' in element && Array.isArray((element as unknown as { elements: unknown }).elements)

const displayedElements = computed(() => {
  if (!props.inline) {
    return props.text.elements
  }

  return props.text.elements.flatMap((element: UmbracoRichTextElement) => {
    if (hasElements(element) && ['div', 'p'].includes(element.tag)) {
      return element.elements ?? []
    }

    return [element]
  })
})

provide('bodyVariant', props.bodyVariant)
</script>

<style lang="scss" scoped>
.rte {
  --list-gap: 0.75rem;

  :deep(.headOffice) {
    color: var(--color-primary-100);
  }

  :deep(ol.counter-reset-list) {
    counter-reset: ordered-list;
    list-style: none;

    > li {
      counter-increment: ordered-list;
      display: inline-flex;
      align-items: flex-start;
      position: relative;
      padding-left: 0;
      gap: var(--list-gap);

      &::before {
        content: counter(ordered-list) '. ';
        font-weight: bold;
        display: inline-block;
        min-width: 0.9rem;
      }

      ol {
        counter-reset: subsection;
        margin-top: 8px;

        li {
          counter-increment: subsection;

          &::before {
            content: counter(ordered-list) '.' counter(subsection);
            font-weight: bold;
            display: inline-block;
            min-width: 0.9rem;
          }
        }
      }
    }
  }

  :deep(ul > li) {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--list-gap);
    padding-left: 0;
    margin-left: 0;

    ul {
      margin-top: 8px;
    }
  }

  :deep(ul:not(.checkmark-list):not(.counter-reset-list):not(.dash-list)) {
    list-style: none;
    margin-left: 0;
    padding-left: 0;

    > li::before {
      content: '●';
      display: inline-flex;
      align-items: start;
      align-self: baseline;
      min-width: 0.85rem;
      font-size: 0.9rem;
      margin-top: 0.02rem;
      vertical-align: baseline;
    }
  }

  :deep(ul.checkmark-list > li::before) {
    content: '';
    display: inline-flex;
    align-self: baseline;
    width: 0.875rem;
    height: 0.875rem;
    margin-top: 0.3rem;
    flex-shrink: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='69' height='67' version='1.2' viewBox='1.509 1 68.356 66.516' role='img'%3E%3Cpath d='M59.2 2.6c-1.3.9-6.7 10.5-12.5 22.2C35.7 46.9 31.2 55 30.1 55c-.8 0-12.4-19-13.7-22.3C15 29 6.3 29.8 2.5 33.9.6 36.1.8 36.7 8.8 50c9.4 15.6 13.6 19 21.1 17 5.7-1.5 9.8-5.4 15.3-14.6 5.7-9.8 23.4-45.1 24.4-48.8.6-2.5.5-2.6-3.8-2.6-2.4 0-5.4.7-6.6 1.6' fill='currentColor'%3E%3C/path%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }

  :deep(ul.dash-list > li) {
    align-items: baseline;

    &::before {
      content: '-';
      display: inline-flex;
      font-weight: normal;
      min-width: 0.875rem;
      flex-shrink: 0;
    }
  }
}
</style>
